import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import MissionService from '../../../../../../../service/MissionService.js';
import { showToastMessage, preventKeyInputs, validateNumberWithoutDecimal, truncateNumber } from '../../../../../../../utils/commonFunctions.js';
import { useMDCockpit } from '../../../../hooks/useMDCockpit';
import { useDraggable } from '../../../../hooks/useDraggable';
import UtcDateTimeField from '../../../SettingLayer/components/UtcDateTimeField';
import { displayToEpoch, epochToDisplay, getUtcNowDisplay, validateUtcDisplay } from '../../../../lib/datetime';
import type { SatGroup, Satellite } from '../../../../types/mission';
import SensorConfigPanel from './SensorConfigPanel';
import styles from './CoverageAnalysisPanel.module.scss';
import { DEFAULT_PROPAGATION_STEP_SECONDS } from '../../../../lib/modelDefaults';
import { CloseSvg, ToggleMinimizeSvg } from '../../../../../../CommonComponents/svgs/Svgs';
import CustomToolTip from '../../../LeftSideBar/Common/CustomToolTip';

// Exact 1:1 Figma Coverage Analysis Header Icon
const CoverageHeaderIcon = () => (
  <svg width="16" height="16" viewBox="0 0 17 17" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M0.5 11.4118V15.3528C0.5 15.9051 0.947716 16.3528 1.5 16.3528H5.47353M0.5 11.4118V5.8818M0.5 11.4118L5.47353 16.3528M7.9004 2.17373L3.31192 3.14529L1.29285 3.57281C0.830649 3.67068 0.5 4.07867 0.5 4.55112V5.8818M0.5 5.8818L11.04 16.3528M3.31192 3.14529L13.758 13.5231M15.666 4.35853L16.3439 1.10254C16.4175 0.749213 16.1039 0.436711 15.7508 0.51147L12.4889 1.20216L7.9004 2.17373M7.9004 2.17373L14.712 8.9408M12.4889 1.20216L15.666 4.35853M13.758 13.5231L14.712 8.9408L15.666 4.35853M13.758 13.5231L13.3346 15.5567C13.2381 16.0204 13.8293 16.3528 13.3556 16.3528H11.04M11.04 16.3528H5.47353" stroke="#F0F0F0" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// Crisp Satellite Pool Icon (16x16)
const SatIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path d="M10.298 5.70199L8.58563 3.98965C8.52481 3.92883 8.42578 3.92854 8.36871 3.9929C7.89645 4.52547 7.68424 5.20801 7.73178 5.87638M7.73178 5.87638C7.77429 6.47399 8.02446 7.06027 8.48208 7.51789C8.91501 7.95083 9.55969 8.20678 10.1236 8.26819C10.8221 8.34426 11.4605 8.12754 12.0077 7.63188C12.0714 7.57414 12.0711 7.47516 12.0103 7.41433L10.298 5.70199M10.298 5.70199L11.2319 4.76805M7.73178 5.87638L6.69531 6.91285M10.1236 8.26819L9.08712 9.30466M6.69531 6.91285L5.23329 5.45083C5.17247 5.39001 5.07386 5.39001 5.01303 5.45083L3.25927 7.2046C3.19844 7.26542 3.19845 7.36404 3.25927 7.42486L4.72129 8.88688M6.69531 6.91285L4.72129 8.88688M4.72129 8.88688L4.30631 9.30185C3.90083 9.70734 3.90083 10.3648 4.30631 10.7702L5.22973 11.6937C5.63521 12.0991 6.29263 12.0991 6.69812 11.6937L7.11309 11.2787M9.08712 9.30466L7.11309 11.2787M9.08712 9.30466L10.5491 10.7667C10.61 10.8275 10.61 10.9261 10.5491 10.9869L8.79537 12.7407C8.73455 12.8015 8.63593 12.8015 8.57511 12.7407L7.11309 11.2787" stroke="#b4b4b4" strokeWidth="0.8" strokeLinecap="round" />
  </svg>
);

// Crisp GeoFeature Pool Icon (16x16)
const GeoIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path d="M5.55753 5.70877C5.45903 5.23699 5.04077 4.8826 4.53974 4.8826C3.96551 4.8826 3.5 5.34811 3.5 5.92235C3.5 6.49658 3.96551 6.96209 4.53974 6.96209C5.11398 6.96209 5.57949 6.49658 5.57949 5.92235C5.57949 5.84914 5.57192 5.7777 5.55753 5.70877ZM4.53974 6.96209V10.4194M9.06028 11.4592C9.06028 12.0334 9.52579 12.4989 10.1 12.4989C10.6743 12.4989 11.1398 12.0334 11.1398 11.4592C11.1398 10.9481 10.7711 10.5232 10.2852 10.4359M9.06028 11.4592C9.06028 10.8849 9.52579 10.4194 10.1 10.4194C10.1632 10.4194 10.2251 10.4251 10.2852 10.4359M9.06028 11.4592L5.57949 11.4591M5.57949 11.4591C5.57949 12.0333 5.11398 12.4988 4.53974 12.4988C3.96551 12.4988 3.5 12.0333 3.5 11.4591C3.5 10.8849 3.96551 10.4194 4.53974 10.4194M5.57949 11.4591C5.57949 10.8849 5.11398 10.4194 4.53974 10.4194M10.2852 10.4359L11.2523 5.56003M10.4374 4.72883C10.5135 5.14579 10.8382 5.47596 11.2523 5.56003C11.3195 5.57367 11.389 5.58083 11.4603 5.58083C12.0345 5.58083 12.5 5.11532 12.5 4.54109C12.5 3.96685 12.0345 3.50134 11.4603 3.50134C10.886 3.50134 10.4205 3.96685 10.4205 4.54109C10.4205 4.60519 10.4263 4.66793 10.4374 4.72883ZM5.55753 5.70877L10.4374 4.72883" stroke="#b4b4b4" strokeWidth="0.8" />
  </svg>
);

const ChipRemoveIcon = () => (
  <svg width="9.6" height="9.6" viewBox="0 0 10 10" fill="none">
    <path d="M1.5 1.5l7 7M8.5 1.5l-7 7" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
  </svg>
);

type SourceObjectType = 'Single' | 'Group';
type PoolItemKind = 'single' | 'group' | 'geofeature';
type PoolItem = { id: string; requestObjectId?: string; name: string; kind?: PoolItemKind; objectType?: SourceObjectType };

function getAnalysisSources(tree: Array<Satellite | SatGroup>): PoolItem[] {
  const out: PoolItem[] = [];
  for (const item of tree) {
    if ((item as SatGroup).type === 'group') {
      const grp = item as SatGroup;
      out.push({ id: grp.id, name: grp.name, kind: 'group', objectType: 'Group' });
      if (Array.isArray(grp.children)) {
        grp.children.forEach(child => {
          out.push({
            id: child.id,
            requestObjectId: child.id,
            name: child.name,
            kind: 'single',
            objectType: 'Single',
          });
        });
      }
    } else {
      const satellite = item as Satellite;
      out.push({
        id: satellite.id,
        requestObjectId: satellite.id,
        name: satellite.name,
        kind: 'single',
        objectType: 'Single',
      });
    }
  }
  return out;
}

interface PoolProps {
  title: string;
  type: 'satellites' | 'geofeatures';
  icon: React.ReactNode;
  selected: PoolItem[];
  onRemove: (id: string) => void;
  onConfigureSensors?: () => void;
  satellites?: Array<Satellite | SatGroup>;
}

const Pool: React.FC<PoolProps> = ({ title, type, icon, selected, onRemove, onConfigureSensors, satellites }) => {
  const placeholderText = type === 'satellites'
    ? 'Select Satellite(s)'
    : 'Select Geofeature(s)';

  // Calculate display count: for groups, count satellites inside; for singles, count as 1
  const displayCount = useMemo(() => {
    if (type !== 'satellites' || !satellites) return selected.length;

    let count = 0;
    selected.forEach(item => {
      if (item.objectType === 'Group') {
        const grp = (satellites || []).find(x => x.id === item.id) as SatGroup;
        if (grp && Array.isArray(grp.children)) {
          count += grp.children.length;
        }
      } else {
        count += 1;
      }
    });
    return count;
  }, [selected, type, satellites]);

  return (
    <div className={styles.pool}>
      <div className={styles.poolHeaderRow}>
        <div className={styles.poolHeaderLeft}>
          <span className={styles.poolIcon}>{icon}</span>
          <span className={styles.poolTitle}>{title} ({displayCount})</span>
        </div>
        {type === 'satellites' && onConfigureSensors && (
          <button
            type="button"
            className={styles.configureSensorsBtn}
            onClick={onConfigureSensors}
            disabled={selected.length === 0}
          >
            Configure Sensors
          </button>
        )}
      </div>

      <div className={styles.poolBox}>
        {selected.length === 0 ? (
          <span className={styles.poolPlaceholder}>{placeholderText}</span>
        ) : (
          <div className={styles.poolChips}>
            {selected.map(item => (
              <span
                key={item.id}
                className={`${styles.poolChip} ${styles[type]}`}
              >
                <span className={styles.chipText}>{item.name}</span>
                <button
                  className={styles.chipRemove}
                  type="button"
                  onClick={() => onRemove(item.id)}
                >
                  <ChipRemoveIcon />
                </button>
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const CoverageAnalysisPanel: React.FC = () => {
  const {
    coverageAnalyserOpen, closeCoverageAnalyser, openSensorConfig, closeSensorConfig, satellites, geoFeatures,
    selectedItemId, selectedItemType, modelData,
    closeAddGS, selectTreeItem, leftPanelCollapsed, selectedCoverageSensors, setSelectedCoverageSensors,
    selectedGeoFeatureIds, toggleSelectedGeoFeature, upsertCoverageAnalysisReport,
  } = useMDCockpit();
  const { mission_id, scenario_id } = useParams<{ mission_id: string; scenario_id: string }>();

  // Track position and pass down to SensorConfigPanel so it drags in 100% sync
  const { style, headerProps, pos } = useDraggable({
    top: 60,
    left: leftPanelCollapsed ? 12 : 236,
  }, coverageAnalyserOpen);

  const [minimized, setMinimized] = useState(false);
  const [running, setRunning] = useState(false);

  useEffect(() => {
    if (coverageAnalyserOpen) {
      setMinimized(false);
    }
  }, [coverageAnalyserOpen]);

  const [selSourceIds, setSelSourceIds] = useState<Set<string>>(new Set());
  const [selGfIds, setSelGfIds] = useState<Set<string>>(new Set());

  const [gridSize, setGridSize] = useState('100');
  const [stepSec, setStepSec] = useState(String(DEFAULT_PROPAGATION_STEP_SECONDS));
  const lastToastTimeRef = React.useRef(0);
  const showThrottledInfoToast = (msg: string) => {
    const now = Date.now();
    if (now - lastToastTimeRef.current > 3000) {
      showToastMessage('info', msg);
      lastToastTimeRef.current = now;
    }
  };
  const [startTime, setStartTime] = useState(getUtcNowDisplay);
  const [durationHrs, setDurationHrs] = useState('48');
  const [durationError, setDurationError] = useState('');

  const allSources = useMemo(() => getAnalysisSources(satellites as Array<Satellite | SatGroup>), [satellites]);
  const sourceById = useMemo(() => new Map(allSources.map(source => [source.id, source])), [allSources]);

  useEffect(() => {
    if (!coverageAnalyserOpen) return;
    const prop = modelData?.propagation as any;
    if (prop?.startEpoch) setStartTime(epochToDisplay(prop.startEpoch));
    if (prop?.startEpoch && prop?.endEpoch && prop.endEpoch > prop.startEpoch) {
      const hours = (prop.endEpoch - prop.startEpoch) / 3600;
      setDurationHrs(Number.isInteger(hours) ? String(hours) : String(Number(hours.toFixed(4))));
    }
    if (prop?.stepSizeSeconds && prop.stepSizeSeconds >= 1 && prop.stepSizeSeconds <= 10) setStepSec(String(prop.stepSizeSeconds));
  }, [coverageAnalyserOpen, modelData?.propagation]);

  const lastProcessedIdRef = React.useRef<string | null>(null);
  const ignoreNextSelectionChangeRef = React.useRef<boolean>(false);

  useEffect(() => {
    if (!coverageAnalyserOpen) return;

    if (ignoreNextSelectionChangeRef.current) {
      ignoreNextSelectionChangeRef.current = false;
      lastProcessedIdRef.current = selectedItemId;
      return;
    }

    if (!selectedItemId || !selectedItemType) {
      if (lastProcessedIdRef.current) {
        const prevId = lastProcessedIdRef.current;
        setSelSourceIds(prev => {
          const n = new Set(prev);
          n.delete(prevId);
          return n;
        });
        setSelGfIds(prev => {
          const n = new Set(prev);
          n.delete(prevId);
          return n;
        });
        lastProcessedIdRef.current = null;
      }
      return;
    }

    if (lastProcessedIdRef.current === selectedItemId) return;
    lastProcessedIdRef.current = selectedItemId;

    if (selectedItemType === 'satellite' || selectedItemType === 'group') {
      if (sourceById.has(selectedItemId)) {
        // Check if we are selecting a child satellite whose parent group is already selected
        const isParentGroupSelected = (satellites || []).some(x => {
          const grp = x as SatGroup;
          if (grp.type === 'group' && Array.isArray(grp.children)) {
            return selSourceIds.has(grp.id) && grp.children.some(c => c.id === selectedItemId);
          }
          return false;
        });

        if (isParentGroupSelected) {
          // Immediately clear the tree selection so it doesn't show as highlighted
          selectTreeItem(null, null);
          return;
        }

        const alreadyInPool = selSourceIds.has(selectedItemId);
        if (alreadyInPool) {
          setSelSourceIds(prev => {
            const n = new Set(prev);
            n.delete(selectedItemId);
            return n;
          });
          selectTreeItem(null, null);
        } else {
          setSelSourceIds(prev => {
            const n = new Set(prev);
            n.add(selectedItemId);

            const clean = new Set(n);
            (satellites || []).forEach(item => {
              const grp = item as SatGroup;
              if (grp.type === 'group' && clean.has(grp.id)) {
                if (Array.isArray(grp.children)) {
                  grp.children.forEach(child => {
                    clean.delete(child.id);
                  });
                }
              }
            });
            return clean;
          });
        }
      }
    } else if (selectedItemType === 'geofeature') {
      const alreadyInPool = selGfIds.has(selectedItemId);
      if (alreadyInPool) {
        setSelGfIds(prev => {
          const n = new Set(prev);
          n.delete(selectedItemId);
          return n;
        });
        selectTreeItem(null, null);
      } else {
        setSelGfIds(prev => {
          const n = new Set(prev);
          n.add(selectedItemId);
          return n;
        });
      }
    }
  }, [selectedItemId, selectedItemType, coverageAnalyserOpen, sourceById]);

  const allGfs = useMemo(() => geoFeatures.map((g: any) => ({ id: g.id, name: g.name, kind: 'geofeature' as const })), [geoFeatures]);

  const selSources = allSources.filter(s => selSourceIds.has(s.id));
  const selGfs = allGfs.filter(g => selGfIds.has(g.id));

  // Compute active individual satellites and their sensor configurations
  const activeSatellites = useMemo(() => {
    const satList: { id: string; name: string; sensors: any[] }[] = [];
    selSources.forEach(source => {
      if (source.objectType === 'Group') {
        const grp = (satellites || []).find(x => x.id === source.id) as SatGroup;
        if (grp && Array.isArray(grp.children)) {
          grp.children.forEach(c => {
            satList.push({ id: c.id, name: c.name, sensors: c.sensors || [] });
          });
        }
      } else {
        let sat: Satellite | undefined;
        for (const item of satellites || []) {
          if ((item as SatGroup).type === 'group') {
            const found = (item as SatGroup).children?.find(c => c.id === source.id);
            if (found) {
              sat = found;
              break;
            }
          } else if ((item as Satellite).id === source.id) {
            sat = item as Satellite;
            break;
          }
        }
        if (sat) {
          satList.push({ id: sat.id, name: sat.name, sensors: sat.sensors || [] });
        } else {
          satList.push({ id: source.id, name: source.name, sensors: [] });
        }
      }
    });
    return satList;
  }, [selSources, satellites]);

  // Automatically select the first sensor of each active satellite if not already set
  useEffect(() => {
    if (!coverageAnalyserOpen || activeSatellites.length === 0) return;
    setSelectedCoverageSensors(prev => {
      let changed = false;
      const next = { ...prev };
      activeSatellites.forEach(sat => {
        const firstSensorId = sat.sensors?.[0]?.sensorId || (sat.sensors?.[0] as any)?.id;
        if (!next[sat.id] && firstSensorId) {
          next[sat.id] = firstSensorId;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [coverageAnalyserOpen, activeSatellites, setSelectedCoverageSensors]);

  const totalAvailableSensorsCount = useMemo(() => {
    return activeSatellites.reduce((acc, curr) => acc + (curr.sensors?.length || 0), 0);
  }, [activeSatellites]);

  const hasValidSensors = useMemo(() => {
    if (activeSatellites.length === 0) return false;
    return activeSatellites.every(sat => {
      return sat.sensors && sat.sensors.length > 0 && Boolean(selectedCoverageSensors[sat.id]);
    });
  }, [activeSatellites, selectedCoverageSensors]);

  const startEpoch = displayToEpoch(startTime);
  const durationNumber = Number(durationHrs);
  const stepSecNumber = Number(stepSec);
  const gridSizeNumber = Number(gridSize);

  const modelStart = modelData?.propagation?.startEpoch;
  const modelEnd = modelData?.propagation?.endEpoch;

  const startTimeError = useMemo(() => {
    const fmtErr = validateUtcDisplay(startTime);
    if (fmtErr) return fmtErr === 'Required' ? '' : fmtErr;
    if (startEpoch !== null && modelStart && modelEnd) {
      if (startEpoch < modelStart) {
        return `Start time cannot be earlier than ${epochToDisplay(modelStart)} UTC`;
      }
      if (startEpoch > modelEnd) {
        return `Start time cannot be later than ${epochToDisplay(modelEnd)} UTC`;
      }
    }
    return '';
  }, [startTime, startEpoch, modelStart, modelEnd]);

  const getDurationErrorText = (durationStr: string, startEpochVal: number | null, modelEndVal?: number, modelStartVal?: number): string => {
    if (!durationStr.trim()) return 'Duration is required';
    const num = Number(durationStr);
    if (!Number.isFinite(num) || num <= 0) return 'Duration should be greater than or equal to 1';
    if (startEpochVal !== null && modelStartVal && modelEndVal) {
      const maxAllowedSec = modelEndVal - startEpochVal;
      const maxAllowedHrs = maxAllowedSec / 3600;
      if (num * 3600 > maxAllowedSec) {
        const formattedMax = Number.isInteger(maxAllowedHrs) ? String(maxAllowedHrs) : String(Number(truncateNumber(maxAllowedHrs, 3)));
        return `Duration cannot exceed remaining model duration (${formattedMax} Hrs)`;
      }
    }
    return '';
  };

  useEffect(() => {
    if (!coverageAnalyserOpen) return;
    const err = getDurationErrorText(durationHrs, startEpoch, modelEnd, modelStart);
    setDurationError(err);
  }, [coverageAnalyserOpen, durationHrs, startEpoch, modelEnd, modelStart]);

  const stepSecError = useMemo(() => {
    if (!stepSec.trim()) return 'Step size is required';
    if (!Number.isFinite(stepSecNumber) || stepSecNumber <= 0) {
      return 'Step size must be a positive number';
    }
    return '';
  }, [stepSec, stepSecNumber]);

  const handleGridSizeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.replace(/[^0-9]/g, '');
    if (raw === '') {
      setGridSize('');
      return;
    }
    const num = Number(raw);
    if (num > 0 && num <= 300) {
      setGridSize(raw);
    } else {
      showThrottledInfoToast('Grid size must be between 50 and 300');
    }
  };

  const handleStepSecChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.replace(/[^0-9]/g, '');
    if (raw === '') {
      setStepSec('');
      return;
    }
    const num = Number(raw);
    if (num >= 0 && num <= 10) {
      setStepSec(raw);
    } else {
      showThrottledInfoToast('Propagation step size must be between 0 and 10 seconds');
    }
  };

  const handleDurationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    const sanitized = val.replace(/[^0-9.]/g, '').replace(/(\..*?)\..*/g, '$1');
    const err = getDurationErrorText(sanitized, startEpoch, modelEnd, modelStart);
    if (err && sanitized.trim() !== '') {
      showThrottledInfoToast(err);
      setDurationError(err);
      return;
    }
    setDurationHrs(sanitized);
    setDurationError(err);
  };

  // Reset all local state when the panel transitions from open → closed
  const wasCoverageOpenRef = React.useRef(false);
  useEffect(() => {
    if (coverageAnalyserOpen) {
      wasCoverageOpenRef.current = true;
      return;
    }
    if (!wasCoverageOpenRef.current) return;
    wasCoverageOpenRef.current = false;
    setSelSourceIds(new Set());
    setSelGfIds(new Set());
    setGridSize('100');
    setStepSec(String(DEFAULT_PROPAGATION_STEP_SECONDS));
    setStartTime(getUtcNowDisplay());
    setDurationHrs('48');
    setDurationError('');
    setMinimized(false);
    setRunning(false);
    setSelectedCoverageSensors({});
    selectTreeItem(null, null);
    lastProcessedIdRef.current = null;
  }, [coverageAnalyserOpen, setSelectedCoverageSensors]);

  if (!coverageAnalyserOpen) return null;

  const isRequiredEmpty =
    selSources.length === 0 ||
    selGfs.length === 0 ||
    !startTime.trim() ||
    !durationHrs.trim() ||
    !stepSec.trim() ||
    !gridSize.trim();

  const runDisabled = running || isRequiredEmpty;

  const handleCloseAll = () => {
    closeSensorConfig();
    closeCoverageAnalyser();
  };

  const handleConfigureSensors = () => {
    if (selSources.length === 0) return;
    if (totalAvailableSensorsCount === 0) {
      showToastMessage('info', 'No sensors available for selected satellites');
      return;
    }
    openSensorConfig();
  };

  const handleRun = async () => {
    if (runDisabled) return;

    if (totalAvailableSensorsCount === 0) {
      showToastMessage('info', 'No sensors available for selected satellites');
      return;
    }

    if (startTimeError || validateUtcDisplay(startTime) !== '' || startEpoch === null) {
      showToastMessage('info', startTimeError || 'Invalid Start Time. Expected format: YYYY-MM-DD HH:MM:SS (UTC)');
      return;
    }

    if (!Number.isFinite(durationNumber) || durationNumber < 1 || durationError) {
      showToastMessage('info', durationError || 'Duration should be greater than or equal to 1');
      return;
    }

    if (!Number.isFinite(stepSecNumber) || stepSecNumber < 1 || stepSecNumber > 10 || stepSecError) {
      showToastMessage('info', stepSecError || 'Propagation step size should be between 1 to 10');
      return;
    }

    if (!Number.isFinite(gridSizeNumber) || gridSizeNumber < 50 || gridSizeNumber > 300) {
      showToastMessage('info', 'Grid Size should be between 50 to 300');
      return;
    }

    if (!mission_id || !scenario_id) {
      showToastMessage('info', !mission_id ? 'Mission Id is required' : 'Scenario Id is required');
      return;
    }

    setRunning(true);
    closeAddGS();
    selectTreeItem(null, null);

    const groupsPayload = selSources.map(source => {
      let sensorIds: string[] = [];
      if (source.objectType === 'Group') {
        const grp = (satellites || []).find(x => x.id === source.id) as SatGroup;
        if (grp && Array.isArray(grp.children)) {
          grp.children.forEach(child => {
            const sId = selectedCoverageSensors[child.id];
            if (sId) sensorIds.push(sId);
          });
        }
      } else {
        const sId = selectedCoverageSensors[source.id];
        if (sId) sensorIds.push(sId);
      }

      return {
        objectId: source.requestObjectId ?? source.id,
        objectType: source.objectType ?? 'Single',
        sensorIds,
      };
    });

    const body = {
      aoiobjects: selGfs.map(gf => ({ aoiId: gf.id })),
      groups: groupsPayload,
      startTime: startEpoch,
      duration: durationNumber,
      gridSize: gridSizeNumber,
      stepSizeSeconds: Math.round(stepSecNumber),
      sunAngle: 0.0,
    };

    try {
      const res = await MissionService.runCoverageAnalysis(mission_id, scenario_id, body);
      const data = res?.data?.data ?? res?.data;
      if (data?.coverageAnalysisId || data?.reportId) {
        upsertCoverageAnalysisReport({
          reportId: String(data.coverageAnalysisId ?? data.reportId),
          name: data.reportName ?? data.name ?? data.report_name ?? '',
          status: String(data.analysisStatus ?? data.status ?? 'queued').toLowerCase(),
          createdAt: data.createdAt ?? data.created_at ?? new Date().toISOString(),
          updatedAt: data.updatedAt ?? data.updated_at ?? new Date().toISOString(),
          raw: data,
        });
      }
      showToastMessage('success', data?.message ?? res?.data?.message ?? 'Coverage analysis started');
      setRunning(false);
      closeAddGS();
      selectTreeItem(null, null);
      handleCloseAll();
    } catch (error) {
      const message = (error as any)?.response?.data?.message ?? (error as Error)?.message ?? 'Coverage analysis failed';
      showToastMessage('error', message);
      setRunning(false);
    }
  };

  return (
    <>
      <div className={styles.panel} style={{ ...style, zIndex: 30, width: 320 }}>
        {/* Header — drag handle */}
        <div className={styles.header} {...headerProps}>
          <div className={styles.headerLeft}>
            <div className={styles.headerIcon}>
              <CoverageHeaderIcon />
            </div>
            <span className={styles.headerTitle}>Coverage Analysis</span>
          </div>
          <div className={styles.headerRight}>
            <CustomToolTip title={minimized ? 'Restore' : 'Minimize'}>
              <button className={styles.headerBtn} type="button" onClick={() => setMinimized(m => !m)}>
                <ToggleMinimizeSvg type={minimized ? 'maximize' : 'minimize'} />
              </button>
            </CustomToolTip>
            <CustomToolTip title="Close">
              <button className={styles.headerBtn} type="button" onClick={handleCloseAll}>
                <CloseSvg />
              </button>
            </CustomToolTip>
          </div>
        </div>

        <div className={`${styles.collapseWrapper} ${minimized ? styles.collapsed : ''}`}>
          <>
            <div className={styles.body}>
              {/* Satellites Pool Section */}
              <div className={styles.sectionBlock}>
                <Pool
                  title="Satellites"
                  type="satellites"
                  icon={<SatIcon />}
                  selected={selSources}
                  onRemove={id => {
                    setSelSourceIds(s => { const n = new Set(s); n.delete(id); return n; });
                    if (selectedItemId === id) {
                      ignoreNextSelectionChangeRef.current = true;
                      selectTreeItem(null, null);
                    } else {
                      const isChildOfRemovedGroup = (satellites || []).some(x => {
                        const grp = x as SatGroup;
                        return grp.type === 'group' && grp.id === id && Array.isArray(grp.children) && grp.children.some(c => c.id === selectedItemId);
                      });
                      const isParentOfRemovedChild = (satellites || []).some(x => {
                        const grp = x as SatGroup;
                        return grp.type === 'group' && grp.id === selectedItemId && Array.isArray(grp.children) && grp.children.some(c => c.id === id);
                      });
                      if (isChildOfRemovedGroup || isParentOfRemovedChild) {
                        ignoreNextSelectionChangeRef.current = true;
                        selectTreeItem(null, null);
                      }
                    }
                  }}
                  onConfigureSensors={handleConfigureSensors}
                  satellites={satellites as Array<Satellite | SatGroup>}
                />
              </div>

              {/* Geofeatures Pool Section */}
              <div className={styles.sectionBlock}>
                <Pool
                  title="Geofeatures"
                  type="geofeatures"
                  icon={<GeoIcon />}
                  selected={selGfs}
                  onRemove={id => {
                    setSelGfIds(s => { const n = new Set(s); n.delete(id); return n; });
                    if (selectedGeoFeatureIds?.includes(id)) {
                      const nextIds = selectedGeoFeatureIds.filter((selectedId) => selectedId !== id);
                      const nextSelectedItemId = nextIds.length > 0 ? nextIds[nextIds.length - 1] : null;
                      if (nextSelectedItemId !== selectedItemId) {
                        ignoreNextSelectionChangeRef.current = true;
                      }
                      toggleSelectedGeoFeature(id);
                    } else if (selectedItemId === id) {
                      ignoreNextSelectionChangeRef.current = true;
                      selectTreeItem(null, null);
                    }
                  }}
                />
              </div>

              {/* Simulation Resolution Section */}
              <div className={`${styles.sectionBlock} ${styles.withDivider}`}>
                <div className={styles.sectionTitleRow}>
                  <span className={styles.sectionTitle}>Simulation Resolution</span>
                </div>
                <div className={styles.rowsList}>
                  <div className={styles.inputRow}>
                    <span className={styles.rowLabel}>Grid Size</span>
                    <div className={styles.inputRoot}>
                      <div className={styles.textContainer}>
                        <input
                          className={styles.fieldInput}
                          type="text"
                          inputMode="numeric"
                          value={gridSize}
                          onKeyDown={validateNumberWithoutDecimal}
                          onChange={handleGridSizeChange}
                        />
                      </div>
                      <span className={styles.unitLabel}>km</span>
                    </div>
                  </div>
                  <div className={styles.inputRow}>
                    <span className={styles.rowLabel}>Propagation Step Size</span>
                    <div className={styles.inputRoot}>
                      <div className={styles.textContainer}>
                        <input
                          className={`${styles.fieldInput} ${stepSecError ? styles.fieldInputError : ''}`}
                          type="text"
                          inputMode="numeric"
                          value={stepSec}
                          onKeyDown={validateNumberWithoutDecimal}
                          onChange={handleStepSecChange}
                        />
                      </div>
                      <span className={styles.unitLabel}>s</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Scenario Details Section */}
              <div className={styles.sectionBlock}>
                <div className={styles.sectionTitleRow}>
                  <span className={styles.sectionTitle}>Scenario Details</span>
                </div>
                <div className={styles.rowsList}>
                  <div className={styles.inputRow}>
                    <span className={styles.rowLabel}>Start Time</span>
                    <div className={styles.inputRoot}>
                      <div className={styles.textContainer}>
                        <div className={styles.dateTimePickerWrap}>
                          <UtcDateTimeField
                            value={startTime}
                            onChange={(display) => setStartTime(display)}
                            error={startTimeError}
                          />
                        </div>
                      </div>
                      <span className={styles.unitLabel}>UTC</span>
                    </div>
                  </div>
                  <div className={styles.inputRow}>
                    <span className={styles.rowLabel}>Duration</span>
                    <div className={styles.inputRoot}>
                      <div className={styles.textContainer}>
                        <input
                          className={`${styles.fieldInput} ${durationError ? styles.fieldInputError : ''}`}
                          type="text"
                          inputMode="decimal"
                          value={durationHrs}
                          onKeyDown={e => preventKeyInputs(e, ['e', 'E', '+', '-'])}
                          onChange={handleDurationChange}
                        />
                      </div>
                      <span className={styles.unitLabel}>Hrs</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className={styles.footer}>
              <button className={styles.cancelBtn} type="button" onClick={handleCloseAll}>Cancel</button>
              <button
                className={`${styles.runBtn} ${!runDisabled ? styles.activeRun : ''} ${running ? styles.runBtnRunning : ''}`}
                type="button"
                onClick={handleRun}
                disabled={runDisabled}
              >
                {running ? 'Running…' : 'Analyze'}
              </button>
            </div>
          </>
        </div>
      </div>

      {/* SensorConfigPanel rendered directly with parentPos sync */}
      {!minimized && <SensorConfigPanel parentPos={pos} selectedSources={selSources} />}
    </>
  );
};

export default CoverageAnalysisPanel;
